import React, { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { IconChevronRight } from '@americanexpress/dls-icons';
import {
  getStickyGlobalHeaderOffset,
  smoothScrollToElement,
  resolveCtaLinkTargets,
} from '@/helpers/utils';
import { getOneCmsLoungeFinderImagesBaseUrl } from '@/selectors/oneCmsSelectors';
import { getLastViewedProduct } from '@/selectors/lastViewedProductSelectors';
import { useCommonContent } from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import {
  TclMigrationFeatures,
  useTCLFeatureFlags,
} from '@/hooks/featureFlagHooks';
import LFLink from '../LFLink';
import Card from './CenturionNetworkTabLounges';

type NavItem = {
  label: string;
  link: string;
};

type FeaturedCard = {
  iata: string;
  title: string;
  subtitle: ReactNode;
  isNewOpening: boolean;
  image: string;
  imageAlt: string;
  link: string;
};

type CenturionNetworkTabContent = {
  navTitle: string;
  navTitleLink: string;
  navItems: NavItem[];
  loungeSectionTitle: string;
  ctaLabel: string;
  ctaLink: string;
  featuredCards: FeaturedCard[];
};

const fallbackContent: CenturionNetworkTabContent = {
  navTitle: 'The Centurion® Network',
  navTitleLink: 'centurion-network/',
  navItems: [
    { label: 'About Centurion Network', link: 'centurion-network/' },
    { label: 'Access', link: 'centurion-network/access/' },
    { label: 'Digital Waitlist', link: 'centurion-network/digital-waitlist/' },
    {
      label: 'Culinary Collective',
      link: 'centurion-network/culinary-collective/',
    },
  ],
  loungeSectionTitle: 'Featured Centurion Lounges',
  ctaLabel: 'View All Centurion Lounges',
  ctaLink: 'centurion-network/',
  featuredCards: [
    {
      iata: 'LAS',
      title: 'Sidecar by The Centurion Lounge',
      subtitle: 'LAS · Harry Reid International Airport',
      isNewOpening: true,
      image: 'las-centurion-sidecar.jpg',
      imageAlt: 'Sidecar by The Centurion Lounge',
      link: 'LAS/Sidecar-by-the-Centurion-Lounge-Concourse-D-MRDikR6Vki/',
    },
    {
      iata: 'ATL',
      title: 'Atlanta',
      subtitle: 'ATL · Hartsfield–Jackson Atlanta International Airport',
      isNewOpening: false,
      image: 'atl-centurion-lounge.jpg',
      imageAlt: 'Atlanta Centurion Lounge',
      link: 'ATL/The-Centurion-Lounge-Concourse-E-KF4QDCUsaM/',
    },
    {
      iata: 'SLC',
      title: 'Salt Lake City',
      subtitle: (
        <>
          SLC · Salt Lake City International
          <br /> Airport
        </>
      ),
      isNewOpening: true,
      image: 'slc-centurion-lounge.jpg',
      imageAlt: 'Salt Lake City Centurion Lounge',
      link: 'SLC/The-Centurion-Lounge-Concourse-B-QwpjoOEs8k/',
    },
  ],
};

export default function CenturionNetwork() {
  const baseOneCmsImgUrl = useSelector(getOneCmsLoungeFinderImagesBaseUrl);
  const lastViewedProduct = useSelector(getLastViewedProduct);
  const { header } = useCommonContent();
  const isCulinaryCollectivePageEnabled = useTCLFeatureFlags(
    TclMigrationFeatures.CULINARY_COLLECTIVE_PAGE
  );
  const content: CenturionNetworkTabContent =
    header?.centurionNetwork || fallbackContent;

  const { featuredCards, navTitle, navTitleLink, navItems, ctaLabel, ctaLink } =
    content;
  const loungesAnchor = '#centurion-network-lounges-header';
  const ctaLinkValue = (ctaLink || 'centurion-network/').trim();
  const { hashSelector, ctaDestination } = resolveCtaLinkTargets(
    ctaLinkValue,
    loungesAnchor
  );

  const handleCtaClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    const el = document.querySelector(hashSelector);
    if (el) {
      event.preventDefault();
      smoothScrollToElement({
        element: el,
        offset: getStickyGlobalHeaderOffset(),
      });
    }
  };

  return (
    <div className="tw-py-6">
      <div className="tw-mx-auto tw-w-full tw-pl-4 tw-pr-0 md_tw-pl-6 md_tw-pr-6 lg_tw-max-w-360 lg_tw-px-8 xl_tw-px-10">
        <div className="tw-flex tw-flex-col tw-items-start tw-gap-10 md_tw-flex-row">
          <div className="tw-w-full md_tw-w-auto md_tw-min-w-80 md_tw-shrink-0">
            <nav>
              <LFLink
                to={navTitleLink || 'centurion-network/'}
                element="header"
                detail="The Centurion Network"
                className="tw-whitespace-nowrap tw-font-sans tw-text-base tw-font-bold tw-text-gray-300"
              >
                {navTitle}
              </LFLink>
              <ul className="tw-mt-4 tw-space-y-4 tw-font-sans tw-text-base tw-font-medium tw-text-blue-bright-400">
                {navItems.map((item) => {
                  if (
                    item.link.includes('culinary-collective') &&
                    !isCulinaryCollectivePageEnabled
                  ) {
                    return null;
                  }
                  return (
                    <li key={item.label}>
                      <LFLink
                        to={item.link}
                        element="header>>The Centurion® Network>>link"
                        detail={item.label}
                        className="tw-text-blue-bright-400 hover_tw-underline"
                      >
                        {item.label}
                      </LFLink>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </div>

          <div className="tw-hidden tw-w-px tw-self-stretch tw-bg-blue-deep-tint-700-t12 md_tw-block" />

          <div className="tw-flex tw-w-full tw-min-w-0 tw-flex-1 tw-flex-col">
            <h2 className="tw-mb-4 tw-font-sans tw-text-base tw-font-bold tw-text-gray-300">
              {content.loungeSectionTitle}
            </h2>

            <div className="scrollbar-hide tw-flex tw-w-full tw-gap-4 tw-overflow-x-auto tw-pb-2 md_tw-grid md_tw-grid-cols-3 md_tw-gap-4 md_tw-overflow-visible md_tw-pb-0 lg_tw-gap-8">
              {featuredCards.map(
                (
                  {
                    iata,
                    title,
                    subtitle,
                    image,
                    imageAlt,
                    isNewOpening,
                    link,
                  },
                  index
                ) => (
                  <div
                    key={iata}
                    className="tw-w-64 tw-shrink-0 tw-snap-start md_tw-w-auto md_tw-shrink"
                  >
                    <LFLink
                      to={`${lastViewedProduct}/${link}`}
                      element={`header>>The Centurion® Network>>Featured Lounge>>position ${index + 1}`}
                      detail={iata}
                    >
                      <Card
                        title={title}
                        subtitle={subtitle}
                        image={`${baseOneCmsImgUrl}${image.replace(/^\/$/, '')}`}
                        imageAlt={imageAlt}
                        isNewOpening={isNewOpening}
                      />
                    </LFLink>
                  </div>
                )
              )}
            </div>

            <LFLink
              to={ctaDestination}
              element="header>>The Centurion® Network>>link"
              detail={ctaLabel}
              onClickHandler={handleCtaClick}
              className="tw-mt-5 tw-w-fit tw-items-center tw-gap-1 tw-font-sans tw-text-base tw-font-medium tw-text-blue-bright-400 hover_tw-underline"
            >
              <span>{ctaLabel}</span>
              <IconChevronRight
                size="xs"
                isFilled
                color="inherit"
                className="tw-fill-blue-bright-400"
              />
            </LFLink>
          </div>
        </div>
      </div>
    </div>
  );
}
